import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import UberCtrl from '@api/Uber/UberCtrl';
import { Order } from '@api/Orders/Order';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';

/**
 * Mandar un delivery con Uber Direct desde el detalle: cotizar → `ModalUberQuote`
 * → crear el envío. ⚠️ `UberServ` corre con `env = 'production'`: es real.
 * **El webhook del server es quien cierra el pedido y otorga sus puntos**; aquí
 * solo se pide el viaje.
 */
const useUberDispatch = (currentOrder: Order | undefined) => {
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const uberCtrl = useMemo(() => new UberCtrl(), []);
    const [quoteData, setQuoteData] = useState<any>(null);
    const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

    const onSendWithUber = async () => {
        if (!currentOrder || !branchOfficeSelected) return;
        if (!currentOrder.deliveryAddress?.location?.lat) {
            showAlertError('La orden no tiene una ubicación de entrega válida.');
            return;
        }
        showLoader();
        try {
            const quote = await uberCtrl.createQuote(currentOrder, branchOfficeSelected);
            setQuoteData(quote);
            setIsQuoteModalOpen(true);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    // Confirmar la cotización y crear el envío.
    const onConfirmUberDelivery = async () => {
        if (!currentOrder || !branchOfficeSelected || !quoteData?.id) return;
        setIsQuoteModalOpen(false);
        showLoader();
        try {
            await uberCtrl.createDelivery(currentOrder, branchOfficeSelected, quoteData.id);
            showAlertMessage('¡Envío creado con Uber exitosamente!');
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onCloseQuoteModal = () => setIsQuoteModalOpen(false);

    return { quoteData, isQuoteModalOpen, onSendWithUber, onConfirmUberDelivery, onCloseQuoteModal };
};

export default useUberDispatch;
